import React, {Dispatch, FC, SetStateAction} from 'react';
import { Modal } from '@mui/material';
import './styled.scss';
import {FilterHeader20} from '../../base/FilterHeader20';
import {IRegularCategory} from '../../../interfaces/regularCategory';
import {CustomCheckbox} from '../CustomCheckbox/CustomCheckbox';
import {WhiteButton} from '../../HomePage/WhiteButton';
import {BlackButton} from '../../HomePage/BlackButton';
import { useTranslation } from '../../../hooks/useTranslation';
import { useCategoryName } from '../../../utils/categoryDisplay';

interface IUpdateRegularCategoriesProps {
    isVisible: boolean,
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    categories: IRegularCategory[],
    handleChangeRegularCategory: (id: number) => void;
    selectedRegularCategories: number[]
    handleSubmitRegularCategories: () => void;
    // The same modal serves Add and Remove; only the wording and the submit
    // handler differ, so the caller supplies them. Defaults keep every existing
    // call site (which spreads the hook and passes nothing else) on "Add".
    title?: string;
    hint?: string;
    applyLabel?: string;
    // Removal only: warn that some of the ticked categories are what gives team
    // members access to these contacts.
    warnOnAccessGrants?: boolean;
}

export const UpdateRegularCategories:
    FC<IUpdateRegularCategoriesProps> = ({
                                             isVisible,
                                             setIsVisible,
                                             categories,
                                             selectedRegularCategories,
                                             handleChangeRegularCategory,
                                             handleSubmitRegularCategories,
                                             title,
                                             hint,
                                             applyLabel,
                                             warnOnAccessGrants = false
    }) => {
    const { p, t } = useTranslation('contacts');
    // Seeded categories are stored under their English name; the list elsewhere
    // (ContactsList, filters) shows them translated, so this modal must too.
    const categoryName = useCategoryName();

    // Team members whose access flows through the categories currently ticked.
    // Counted per category and summed, so "2 people" means two grants affected,
    // not two categories.
    const affectedGrantUsers = !warnOnAccessGrants ? 0 : (categories ?? [])
        .filter((category) => selectedRegularCategories.includes(category.id))
        .reduce((total, category) => total + (category.access_grant_users ?? 0), 0);
    return (
        <Modal
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
            open={isVisible}
            onClose={() => {
                setIsVisible(prevState => !prevState)
            }}
        >
            <div className='update-regular-categories'>
                <FilterHeader20
                    title={title ?? p("update_regular_categories_title")}
                    disableUnderline
                    onClose={() => setIsVisible(!isVisible)}
                />
                {hint && (
                    <p className='update-regular-categories__hint'>{hint}</p>
                )}
                {affectedGrantUsers > 0 && (
                    <p className='update-regular-categories__notice'>
                        {p("remove_categories_access_notice", { count: affectedGrantUsers })}
                    </p>
                )}
                <div className='update-regular-categories__body'>
                    {categories?.map((category) => (
                        <div
                            key={category.id}
                            className='update-regular-categories__item'>
                                <CustomCheckbox
                                    onChange={(id) => {
                                        if(!id) return ;
                                        handleChangeRegularCategory(id)
                                    }}
                                    checked={selectedRegularCategories.includes(category.id)}
                                    id={category.id}
                                />
                            <span>
                                {categoryName(category?.name)}
                            </span>
                        </div>
                    ))}
                </div>
                <div className='update-scheduled-categories__footer'>
                    <WhiteButton
                        forModal
                        title={t("general.cancel")}
                        onClick={() => {
                            setIsVisible(prevState => !prevState)
                        }} />
                    <BlackButton
                        forModal
                        title={applyLabel ?? t("general.apply")}
                        onClick={handleSubmitRegularCategories}
                    />
                </div>
            </div>
        </Modal>
    )
}